import { MoreOutlined } from '@ant-design/icons';
import { offerStatuses } from '@features/offers/constants/offerStatuses.const';
import { useOfferBulkImport } from '@features/products/hooks/useOfferBulkImport';
import { useQuery } from '@tanstack/react-query';
import { PageListProps } from '@templates';
import { Button, Dropdown, MenuProps, Tag } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import OpenNotification from 'src/app/components/antd/OpenNotification';
import { usePaginationFilter } from 'src/app/hooks/usePaginationFilter';

import { offerApi } from '../apis/offerApis';
import { OfferListItem } from '../interfaces/offersInterface';
import { buildOfferCheckoutLink, linkToOfferScreen } from '../utils/helpers';
import { offerTableFactory } from '../utils/offerTableFactory';
import { renderOfferNameWithTags } from '../utils/renderOfferNameWithTags';
import { useOfferProperties } from './useOfferProperties';
import { useOfferUpsellFlowOptions } from './useOfferUpsellFlowOptions';
import { useProductById } from './useProductById';

const colorByStatus = {
  active: 'green',
  expired: 'magenta',
  scheduled: 'orange',
};

const OFFER_PAGE_SIZE = 25;
const DEFAULT_STATUS_FILTER: OfferListItem['status'][] = [
  'active',
  'scheduled',
];

export default function useOfferPage() {
  const { productId } = useParams();
  const {
    tableColumnSortOrderSymbol,
    currentFilter,
    handleChangeFilter,
    handleChangeOrder,
    handleChangePagination,
  } = usePaginationFilter<OfferListItem>(OFFER_PAGE_SIZE);
  const hasInitialFiltersRef = useRef(
    Object.keys(currentFilter.filter || {}).length > 0
  );
  const hasAppliedDefaultStatusRef = useRef(false);
  const { ActionsOfferBulk, DrawerOfferBulk } = useOfferBulkImport();
  const { offerProperties, isLoading: isLoadingOfferProperties } =
    useOfferProperties();
  const [offerToExpire, setOfferToExpire] = useState<OfferListItem | undefined>(
    undefined
  );
  const [isLoadingExpire, setIsLoadingExpire] = useState(false);

  const { product, isLoading: isLoadingProduct } = useProductById(
    Number(productId)
  );
  const upsellFlowOptions = useOfferUpsellFlowOptions();

  const {
    data: offerList,
    isFetching: isLoadingOffers,
    refetch,
  } = useQuery({
    queryKey: [
      'offers',
      productId,
      currentFilter.limit,
      currentFilter.page,
      currentFilter.order,
      (() => {
        const nextFilter = { ...currentFilter.filter };
        if (nextFilter.status === '') {
          delete nextFilter.status;
        }
        return nextFilter;
      })(),
      !hasInitialFiltersRef.current && !hasAppliedDefaultStatusRef.current,
    ],
    queryFn: async () => {
      const nextFilter = { ...currentFilter.filter };

      if (nextFilter.status === '') {
        delete nextFilter.status;
      }

      const response = await offerApi.getPaginatedOffers(productId ?? '', {
        ...currentFilter,
        filter:
          !hasInitialFiltersRef.current && !hasAppliedDefaultStatusRef.current
            ? { status: DEFAULT_STATUS_FILTER }
            : nextFilter,
      });
      return offerTableFactory(response);
    },
    enabled: !!productId,
  });

  const filter = useMemo(() => {
    if (!hasInitialFiltersRef.current && !hasAppliedDefaultStatusRef.current) {
      return { status: DEFAULT_STATUS_FILTER };
    }

    return currentFilter.filter;
  }, [currentFilter.filter]);

  useEffect(() => {
    if (hasInitialFiltersRef.current || hasAppliedDefaultStatusRef.current) {
      return;
    }

    hasAppliedDefaultStatusRef.current = true;
    handleChangeFilter({ status: DEFAULT_STATUS_FILTER });
  }, [handleChangeFilter]);

  const isLoading = useMemo(
    () => isLoadingProduct || isLoadingOffers || isLoadingOfferProperties,
    [isLoadingProduct, isLoadingOffers, isLoadingOfferProperties]
  );

  const navigate = useNavigate();

  const handleExpireOffer = useCallback((offerId: string) => {
    if (!productId) return;
    setIsLoadingExpire(true);
    offerApi
      .expireOffer(offerId)
      .then(() => {
        OpenNotification(false, 'Oferta expirada com sucesso.');
        refetch();
      })
      .catch(() => {
        OpenNotification(
          true,
          'Não foi possível expirar a oferta, tente novamente.'
        );
      })
      .finally(() => {
        setOfferToExpire(undefined);
        setIsLoadingExpire(false);
      });
  }, []);

  const headerColumns: PageListProps<OfferListItem>['columns'] = useMemo(
    () => [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        width: '100px',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['id'],
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
      },
      {
        key: 'name',
        title: 'Nome da Oferta',
        dataIndex: 'name',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['name'],
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
        render: (text, item) => {
          return renderOfferNameWithTags(text, item);
        },
      },
      {
        key: 'fullPrice',
        title: 'Valor',
        dataIndex: 'fullPrice',
        width: '150px',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['fullPrice'],
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
      },
      {
        key: 'typeId',
        title: 'Tipo',
        dataIndex: 'typeId',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['typeId'],
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
        render: (typeId: OfferListItem['typeId']) => {
          return (
            <div>{offerProperties?.types.find(t => t.id === typeId)?.name}</div>
          );
        },
      },
      {
        key: 'acquisitionId',
        title: 'Aquisição',
        dataIndex: 'acquisitionId',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['acquisitionId'],
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
        render: (acquisitionId: OfferListItem['acquisitionId']) => {
          return (
            <div>
              {
                offerProperties?.acquisitionTypes.find(
                  a => a.id === acquisitionId
                )?.name
              }
            </div>
          );
        },
      },
      {
        key: 'seasonalityId',
        title: 'Sazonal',
        dataIndex: 'seasonalityId',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['seasonalityId'],
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
        render: (seasonalityId: OfferListItem['seasonalityId']) => {
          return (
            <div>
              {
                offerProperties?.seasonalities.find(s => s.id === seasonalityId)
                  ?.name
              }
            </div>
          );
        },
      },
      {
        key: 'flowId',
        title: 'Funil de upsell',
        dataIndex: 'flow',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['flowId'],
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
        render: (upsellFlow: OfferListItem['flow']) => {
          return (
            <div>
              {upsellFlow ? `${upsellFlow.id} - ${upsellFlow.name}` : '-'}
            </div>
          );
        },
      },
      {
        key: 'startDate',
        title: 'Início',
        dataIndex: 'startDate',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['startDate'],
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
      },
      {
        key: 'endDate',
        title: 'Expiração',
        dataIndex: 'endDate',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['endDate'],
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
        render: (endDate: OfferListItem['endDate']) => {
          return <div>{endDate ?? '-'}</div>;
        },
      },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'status',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['status'],
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
        render: (status: OfferListItem['status']) => {
          return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Tag color={colorByStatus[status]}>{offerStatuses[status]}</Tag>
            </div>
          );
        },
      },
      {
        title: 'Ações',
        key: 'action',
        dataIndex: '',
        width: '80px',
        align: 'center',
        render: (item: OfferListItem) => {
          const menuItems: MenuProps['items'] = [
            {
              key: 'view',
              label: 'Visualizar',
              onClick: (e: any) => {
                e.domEvent.stopPropagation();
                navigate(
                  `/products/${product?.id}/offers/${item.id}?type=view`
                );
              },
            },
            {
              key: 'edit',
              label: 'Editar',
              onClick: (e: any) => {
                e.domEvent.stopPropagation();
                navigate(linkToOfferScreen(item.id, product?.id ?? 0, 'edit'));
              },
              disabled: !product || product?.active === false,
            },
            {
              key: 'copy',
              label: 'Duplicar',
              onClick: (e: any) => {
                e.domEvent.stopPropagation();
                navigate(
                  `/products/${product?.id}/offers/${item.id}?type=duplicate`
                );
              },
              disabled: !product || product?.active === false,
            },
            {
              key: 'link',
              label: 'Copiar Link',
              onClick: () =>
                void navigator.clipboard.writeText(
                  buildOfferCheckoutLink(product?.id, item.id)
                ),
            },
            {
              key: 'expire',
              label: 'Expirar Oferta (inativar)',
              onClick: () => {
                setOfferToExpire(item);
              },
              disabled:
                !product ||
                product?.active === false ||
                (item.endDate
                  ? dayjs(item.endDate, 'DD/MM/YYYY').isBefore(dayjs())
                  : true),
            },
          ];

          return (
            <Dropdown
              trigger={['click']}
              arrow={false}
              menu={{ items: menuItems }}
            >
              <ButtonMenu
                type='text'
                data-testid={`actions-dropdown-${item.id}`}
              >
                <MoreOutlined />
              </ButtonMenu>
            </Dropdown>
          );
        },
      },
    ],
    [handleChangeOrder, navigate, product, setOfferToExpire, offerProperties]
  );

  const actionsOfferBulk = useMemo(() => {
    return <ActionsOfferBulk />;
  }, [ActionsOfferBulk]);

  const drawerOfferBulk = useMemo(() => {
    return <DrawerOfferBulk onSuccessFinishImport={refetch} />;
  }, [DrawerOfferBulk, refetch]);

  return {
    product,
    offerList,
    handleChangeFilter,
    handleChangePagination,
    pageSize: currentFilter.limit || OFFER_PAGE_SIZE,
    currentPage: currentFilter.page || 1,
    filter,
    headerColumns,
    isLoading,
    offerProperties,
    offerToExpire,
    setOfferToExpire,
    handleExpireOffer,
    isLoadingExpire,
    actionsOfferBulk,
    drawerOfferBulk,
    upsellFlowOptions,
  };
}

const ButtonMenu = styled(Button)`
  &:focus {
    color: #3c89e8 !important;
  }
`;
